import EastRoundedIcon from "@mui/icons-material/EastRounded"
import {
    Avatar,
    Box,
    Divider,
    Stack,
    SvgIconProps,
    Typography,
    useTheme,
} from "@mui/material"
import { ComponentType, ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import AppPaper from "../surfaces/appPaper"

interface ISingleKpiCardProps {
    title: string
    value: string | number
    Icon: ComponentType<SvgIconProps>
    link?: string
}

/**
 * Single KPI card with an icon, title, value and optional link
 */
const SingleKpiCard = ({
    title,
    value,
    Icon,
    link,
}: ISingleKpiCardProps): ReactElement => {
    const theme = useTheme()
    const navigate = useNavigate()

    return (
        <AppPaper
            onClick={link ? () => navigate(link) : undefined}
            sx={{
                width: "100%",
                cursor: link ? "pointer" : "cursor",
                height: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexGrow: 1,
                    height: "100%",
                    padding: 0,
                    alignItems: "center",
                }}
            >
                <Stack />

                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    alignItems="center"
                    justifyContent="flex-start"
                    paddingRight={GRID_SPACING}
                >
                    <Avatar
                        sx={{
                            background:
                                theme.palette.mode === "dark"
                                    ? theme.palette.background.contentBackground
                                    : theme.palette.primary.light,
                            width: "70px",
                            height: "70px",
                            mr: 1,
                        }}
                    >
                        <Icon fontSize="large" color="primary" />
                    </Avatar>
                </Stack>

                <Stack flexDirection="column">
                    <Typography
                        variant="body1"
                        fontWeight="500"
                        color="text.secondary"
                        marginBottom={0.5}
                    >
                        {title}
                    </Typography>

                    <Typography variant="h4">
                        {value !== undefined ? value : "N/A"}
                    </Typography>
                </Stack>
                <Divider />
                {link && (
                    <Stack
                        direction="row"
                        spacing={GRID_SPACING}
                        justifyContent="flex-end"
                        alignItems="flex-end"
                        flexGrow={1}
                    >
                        <AppButton
                            endIcon={<EastRoundedIcon />}
                            onClick={() => navigate(link)}
                        >
                            <Typography>View</Typography>
                        </AppButton>
                    </Stack>
                )}
            </Box>
        </AppPaper>
    )
}

SingleKpiCard.defaultProps = {
    link: undefined,
}

export default SingleKpiCard
